/**
 * §P.1 · Añadir o editar un tutor.
 *
 * **Es lo que mete a una familia en la app**, y el otro extremo ya existe: la
 * familia canjea en `/auth/invitations/verify` y `/accept`. Por eso `BFF_BO.md`
 * §7.1 lo llama el círculo completo.
 *
 * Las tres reglas que esta pantalla tiene que respetar:
 *
 * **1 · Modo abuelo y facturación son incompatibles.** `access_level: 'basic'` con
 * `receives_billing: true` es un `422`. La casilla se deshabilita y se dice por
 * qué: el modo abuelo existe para seguir el progreso **sin** ver el dinero, así
 * que dirigirle el recibo produce una factura que esa persona no puede abrir.
 *
 * **2 · «Contacto principal» no falla si ya hay otro.** Marcarlo **degrada al
 * anterior** en la misma transacción, así que se ofrece sin comprobar nada antes.
 *
 * **3 · El nombre técnico no se enseña.** `basic` no le dice nada a un club; «ve
 * progreso y avisos, no pagos ni ficha médica», sí.
 *
 * **4 · El propio alumno, solo si es mayor de edad** (13-10-2026). Un adulto sin nadie
 * detrás no recibía los avisos ni podía firmar sus consentimientos: ahora puede ser
 * su propio tutor. La opción solo sale con 18 años o más, y entonces el acceso es
 * completo y «puede recoger» desaparece.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';
import type { Tutor } from '@/club/alumnos/tipos';
import {
  ajustar,
  ajustarAlPropio,
  puedeSerSuPropioTutor,
  puedeRecibirFacturacion,
  type Acceso,
} from '@/club/alumnos/tutores/reglas';

export type Parentesco =
  'madre' | 'padre' | 'tutor' | 'abuelo' | 'otro' | 'propio';

const PARENTESCOS: readonly { valor: Parentesco; etiqueta: string }[] = [
  { valor: 'madre', etiqueta: 'Madre' },
  { valor: 'padre', etiqueta: 'Padre' },
  { valor: 'tutor', etiqueta: 'Tutor o tutora legal' },
  { valor: 'abuelo', etiqueta: 'Abuelo o abuela' },
  { valor: 'otro', etiqueta: 'Otro' },
  { valor: 'propio', etiqueta: 'El propio alumno (mayor de edad)' },
];

/** El parentesco en palabras, para la lista de tutores. */
export const etiquetaDeParentesco = (valor: string | null): string =>
  valor === 'propio'
    ? 'El propio alumno'
    : (PARENTESCOS.find((p) => p.valor === valor)?.etiqueta ??
      'Sin parentesco indicado');

interface Campos {
  email: string;
  first_name: string;
  relationship: Parentesco;
  access_level: Acceso;
  is_primary: boolean;
  can_pickup: boolean;
  receives_billing: boolean;
}

const desdeTutor = (t: Tutor | null): Campos => ({
  email: t?.email ?? '',
  first_name: t?.first_name ?? '',
  relationship: (t?.relationship as Parentesco) ?? 'madre',
  access_level: (t?.access_level as Acceso) ?? 'full',
  is_primary: t?.is_primary ?? false,
  can_pickup: t?.can_pickup ?? true,
  receives_billing: t?.receives_billing ?? false,
});

/** Lo que devuelve el `POST`: el tutor y si se mandó correo. */
interface Alta {
  readonly guardian: Tutor;
  readonly invitation_sent: boolean;
}

export function FormularioDeTutor({
  club,
  alumnoId,
  nombreDelAlumno,
  nombreDePila,
  edadDelAlumno,
  tutor,
  alTerminar,
  alCancelar,
}: {
  club: string;
  alumnoId: string;
  nombreDelAlumno: string;
  /** Su nombre, para proponerlo cuando el tutor es el propio alumno. */
  nombreDePila: string;
  /** Para ofrecer «el propio alumno», que solo vale con 18 años o más. */
  edadDelAlumno: number | null;
  /** `null` para añadir uno nuevo. */
  tutor: Tutor | null;
  alTerminar: () => void;
  alCancelar: () => void;
}) {
  const cache = useQueryClient();
  const editando = tutor !== null;

  const [campos, setCampos] = useState<Campos>(() => desdeTutor(tutor));
  const [problema, setProblema] = useState<Problema | null>(null);
  const [alta, setAlta] = useState<Alta | null>(null);

  const esAbuelo = !puedeRecibirFacturacion(campos.access_level);
  const propio = campos.relationship === 'propio';
  // La opción sale si el alumno es mayor de edad, o si este vínculo ya lo era.
  const ofrecePropio = puedeSerSuPropioTutor(edadDelAlumno) || propio;

  // `ajustar` apaga la facturación si se elige «solo seguimiento», y `ajustarAlPropio`
  // deja al propio alumno con acceso completo y sin «puede recoger»: así no se manda
  // una combinación que el servidor va a rechazar con un 422. Las reglas viven en
  // `tutores/reglas.ts`, donde se pueden probar sin pintar nada.
  const cambiar = (parcial: Partial<Campos>) =>
    setCampos((c) => {
      const siguiente = ajustarAlPropio(ajustar({ ...c, ...parcial }));
      // Si el tutor es el propio alumno, su nombre ya se sabe.
      return siguiente.relationship === 'propio' && !siguiente.first_name.trim()
        ? { ...siguiente, first_name: nombreDePila }
        : siguiente;
    });

  const guardar = useMutation({
    mutationFn: async (): Promise<Alta> => {
      const comun = {
        relationship: campos.relationship,
        is_primary: campos.is_primary,
        can_pickup: campos.can_pickup,
        receives_billing: campos.receives_billing,
        access_level: campos.access_level,
      };

      if (editando) {
        const r = await api.v1
          .clubs({ club })
          .students({ studentId: alumnoId })
          .guardians({ guardianId: tutor.user_id })
          .patch(comun);
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        return { guardian: r.data as unknown as Tutor, invitation_sent: false };
      }

      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .guardians.post(
          {
            email: campos.email.trim(),
            first_name: campos.first_name.trim() || null,
            ...comun,
          },
          {
            // 🔒 Manda un correo: un reintento no puede invitar dos veces.
            headers: {
              'idempotency-key': `tutor-${alumnoId}-${campos.email.trim().toLowerCase()}`,
            },
          }
        );

      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Alta;
    },

    onSuccess: (resultado) => {
      setProblema(null);
      void cache.invalidateQueries({ queryKey: ['tutores', club, alumnoId] });

      // En la edición no hay nada que contar: se vuelve y ya.
      if (editando) alTerminar();
      else setAlta(resultado);
    },

    onError: (e) => setProblema(aProblema(e)),
  });

  // ── Después del alta: los tres casos ─────────────────────────────────────
  if (alta) {
    const nombre =
      [alta.guardian.first_name, alta.guardian.last_name]
        .filter(Boolean)
        .join(' ') || alta.guardian.email;

    return (
      <div className="hueco">
        {alta.invitation_sent ? (
          <>
            <h2>Invitación enviada</h2>
            <p>
              Le hemos mandado el enlace a <b>{alta.guardian.email}</b>. Cuando
              lo abra,{' '}
              {propio
                ? 'verá sus clases, su progreso y los avisos del club en su app'
                : `verá a ${nombreDelAlumno} en su app`}
              . Aquí aparecerá como «ya usa la app» en cuanto entre.
            </p>
          </>
        ) : (
          /*
           * El tercer caso, y enseñar «invitación enviada» aquí sería mentir: no
           * se ha mandado nada, y con razón — esta persona lleva tiempo usando el
           * club, es el segundo hijo.
           */
          <>
            <h2>{nombre} ya tenía cuenta</h2>
            <p>
              No le hemos mandado ninguna invitación porque ya está dentro: le
              aparecerá {nombreDelAlumno} la próxima vez que abra la app.
            </p>
          </>
        )}
        <div className="hueco-accion">
          <button type="button" onClick={alTerminar}>
            Volver a la ficha
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        guardar.mutate();
      }}
    >
      <h2>{editando ? 'Editar tutor' : 'Añadir tutor'}</h2>

      {!editando && (
        <p className="tenue">
          Al guardar le llega un correo con un enlace para entrar en la app de
          familias. Si ya tiene cuenta en el club, no se le manda nada.
        </p>
      )}

      <div className="rejilla">
        <label>
          Correo
          <input
            type="email"
            required
            // El correo identifica a la persona: cambiarlo sería otro tutor.
            disabled={editando}
            value={campos.email}
            onChange={(e) => cambiar({ email: e.target.value })}
          />
        </label>

        <label>
          Nombre
          <input
            placeholder={editando ? '' : 'Opcional'}
            disabled={editando}
            value={campos.first_name}
            onChange={(e) => cambiar({ first_name: e.target.value })}
          />
        </label>

        <label>
          Parentesco
          <select
            value={campos.relationship}
            onChange={(e) =>
              cambiar({ relationship: e.target.value as Parentesco })
            }
          >
            {PARENTESCOS.filter(
              (p) => p.valor !== 'propio' || ofrecePropio
            ).map((p) => (
              <option key={p.valor} value={p.valor}>
                {p.etiqueta}
              </option>
            ))}
          </select>
        </label>
      </div>

      {propio && (
        <p className="nota">
          <strong>El alumno, con su propia cuenta.</strong> Escribe su correo:
          le llegarán los avisos del club, firmará sus consentimientos y, si es
          su pagador, verá sus recibos.
        </p>
      )}

      {!propio && (
        <fieldset className="opciones">
          <legend>Qué puede ver y hacer</legend>

          <label className="opcion">
            <input
              type="radio"
              name="acceso"
              checked={!esAbuelo}
              onChange={() => cambiar({ access_level: 'full' })}
            />
            <span>
              <strong>Acceso completo</strong>
              <em>Progreso, avisos, pagos y ficha médica.</em>
            </span>
          </label>

          <label className="opcion">
            <input
              type="radio"
              name="acceso"
              checked={esAbuelo}
              onChange={() => cambiar({ access_level: 'basic' })}
            />
            <span>
              {/* «Modo abuelo» es como lo llamamos nosotros; aquí se dice qué hace. */}
              <strong>Solo seguimiento</strong>
              <em>
                Ve progreso y avisos; no ve pagos ni ficha médica. Para abuelos
                y familia que quiere estar al tanto sin entrar en el dinero.
              </em>
            </span>
          </label>
        </fieldset>
      )}

      <fieldset className="opciones">
        <legend>Además</legend>

        <label className="opcion">
          <input
            type="checkbox"
            checked={campos.is_primary}
            onChange={(e) => cambiar({ is_primary: e.target.checked })}
          />
          <span>
            <strong>Contacto principal</strong>
            {/* Marcarlo degrada al anterior en la misma transacción: no falla. */}
            <em>Si ya hay otro, pasa a serlo esta persona.</em>
          </span>
        </label>

        {!propio && (
          <label className="opcion">
            <input
              type="checkbox"
              checked={campos.can_pickup}
              onChange={(e) => cambiar({ can_pickup: e.target.checked })}
            />
            <span>
              <strong>Puede recoger</strong>
              <em>Autorizada a llevarse al alumno al terminar la clase.</em>
            </span>
          </label>
        )}

        <label className={`opcion${esAbuelo ? ' opcion-apagada' : ''}`}>
          <input
            type="checkbox"
            checked={campos.receives_billing}
            disabled={esAbuelo}
            onChange={(e) => cambiar({ receives_billing: e.target.checked })}
          />
          <span>
            <strong>Recibe la facturación</strong>
            <em>
              {esAbuelo
                ? 'No se puede con «solo seguimiento»: sería mandarle un recibo que no puede abrir.'
                : 'Los recibos del alumno van a su nombre y le llegan a ella.'}
            </em>
          </span>
        </label>
      </fieldset>

      {problema && <PanelError problema={problema} />}

      <div className="acciones">
        <button type="submit" disabled={guardar.isPending}>
          {guardar.isPending
            ? 'Guardando…'
            : editando
              ? 'Guardar cambios'
              : 'Añadir e invitar'}
        </button>
        <button
          type="button"
          className="btn-plano"
          onClick={alCancelar}
          disabled={guardar.isPending}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
